import React, { useCallback, useState } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import RefreshOutlinedIcon from '@mui/icons-material/RefreshOutlined';
import ConfirmDialog from '../../../../../components/ConfirmDialog/ConfirmDialog';
import EditionCard from './EditionCard';
import EditionEditorDialog from './EditionEditorDialog';
import EditionPreviewDialog from './EditionPreviewDialog';
import EditionRecipientsDialog from './EditionRecipientsDialog';
import ScheduleDialog from './ScheduleDialog';
import useEditionPreview from '../../hooks/useEditionPreview';
import useEditionPublishing from '../../hooks/useEditionPublishing';
import useEditions from '../../hooks/useEditions';
import { editionTitle } from '../../changelogAdminConfig';

/**
 * Aba Envios: o "Próximo envio" (rascunho sempre presente) no topo e os demais envios,
 * com o editor, prévia, agendamento, envio imediato, cancelamento e destinatários.
 * @param {{ notify: Function }} props
 */
function EditionsTab({ notify }) {
  const { nextEdition, otherEditions, recipientsEstimate, isLoading, error, refresh, reloadSilently } = useEditions();
  const [editingId, setEditingId] = useState(null);
  const [recipientsEdition, setRecipientsEdition] = useState(null);

  const onDone = useCallback(() => {
    setEditingId(null);
    reloadSilently();
  }, [reloadSilently]);

  const previewer = useEditionPreview(notify);
  const publishing = useEditionPublishing({ notify, onDone });

  const handleSaved = (manual) => {
    reloadSilently();
    if (manual) notify('Envio salvo.', 'success');
  };

  const estimate = recipientsEstimate ?? '—';
  const cardProps = {
    onEdit: (edition) => setEditingId(edition.id),
    onPreview: previewer.openPreview,
    onShowRecipients: setRecipientsEdition,
  };

  return (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2, mb: 2 }}>
        <Typography variant="body2" color="text.secondary">
          Destinatários estimados: <strong>{estimate}</strong>
        </Typography>
        <Button variant="outlined" startIcon={<RefreshOutlinedIcon />} onClick={refresh} disabled={isLoading}>
          Atualizar
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress size={32} /></Box>
      ) : (
        <>
          {nextEdition && <EditionCard edition={nextEdition} isNext {...cardProps} />}
          {otherEditions.length > 0 && (
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mt: 3, mb: 1.5 }}>Envios</Typography>
          )}
          {otherEditions.map((edition) => <EditionCard key={edition.id} edition={edition} {...cardProps} />)}
        </>
      )}

      <EditionEditorDialog
        editionId={editingId}
        busy={Boolean(publishing.busy)}
        isSendingPreview={previewer.isSendingPreview}
        onClose={() => setEditingId(null)}
        onSaved={handleSaved}
        actions={{
          preview: previewer.openPreview,
          sendPreview: previewer.sendPreview,
          schedule: publishing.askSchedule,
          unschedule: publishing.unschedule,
          sendNow: publishing.askSendNow,
          cancel: publishing.askCancel,
        }}
      />

      <EditionPreviewDialog {...previewer.preview} onClose={previewer.closePreview} />
      <EditionRecipientsDialog edition={recipientsEdition} onClose={() => setRecipientsEdition(null)} />

      <ScheduleDialog
        edition={publishing.schedule.edition}
        warnings={publishing.schedule.warnings}
        error={publishing.schedule.error}
        busy={publishing.busy === 'schedule'}
        onClose={publishing.closeSchedule}
        onSubmit={publishing.submitSchedule}
      />

      <ConfirmDialog
        isOpen={Boolean(publishing.sendNowEdition)}
        title="Enviar agora?"
        message={publishing.sendNowEdition ? (
          <>
            O envio <strong>{editionTitle(publishing.sendNowEdition)}</strong> vai para cerca de <strong>{estimate}</strong> pessoa(s).
            As novidades dele que ainda não estão publicadas são publicadas agora.
          </>
        ) : null}
        confirmLabel="Enviar agora"
        variant="warning"
        loading={publishing.busy === 'send-now'}
        onConfirm={publishing.confirmSendNow}
        onCancel={publishing.closeSendNow}
      />

      <ConfirmDialog
        isOpen={Boolean(publishing.cancelEdition)}
        title="Cancelar este envio?"
        message={publishing.cancelEdition ? (
          <>O envio <strong>{editionTitle(publishing.cancelEdition)}</strong> não sai mais. As novidades continuam no catálogo.</>
        ) : null}
        confirmLabel="Cancelar envio"
        cancelLabel="Voltar"
        variant="danger"
        loading={publishing.busy === 'cancel'}
        onConfirm={publishing.confirmCancel}
        onCancel={publishing.closeCancel}
      />
    </>
  );
}

export default EditionsTab;
